import { Link, Navigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ChevronLeft, AlertCircle } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import { useAuth } from "@/lib/auth";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignInOwner() {
  const backend = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${backend}/api/auth/google/login?intent=owner`;
}

const ERROR_COPY = {
  invalid_state: "Sign-in session expired. Please try again.",
  expired_state: "Sign-in session expired. Please try again.",
  token_exchange: "Google sign-in failed. Please try again.",
  no_id_token: "Google sign-in failed. Please try again.",
  invalid_id_token: "Google sign-in failed. Please try again.",
  aud_mismatch: "Configuration error. Contact support.",
  iss_mismatch: "Configuration error. Contact support.",
  email_not_verified: "Your Google email must be verified.",
  network: "Network error. Please try again.",
  missing_code: "Sign-in was cancelled.",
  access_denied: "Sign-in was cancelled.",
};

export default function OwnerLogin() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const error = params.get("error");
  const pending = params.get("pending");
  const pendingEmail = params.get("email") || "";

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
        <div className="label">Loading…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/app" replace />;

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <div
        className="mx-auto flex flex-col"
        style={{ maxWidth: 460, minHeight: "100vh", borderLeft: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}
      >
        <div className="p-5">
          <Link to="/" className="inline-flex items-center gap-1.5 label-tiny hover:text-white" data-testid="back-home">
            <ChevronLeft size={14} /> getrolloss.app
          </Link>
        </div>
        <div className="flex-1 flex flex-col justify-center px-7 pb-16">
          <LogoLockup size={32} beltWidth={260} />
          <div className="mt-12">
            <div className="label" style={{ color: "var(--green)" }}>Owner workspace</div>
            <h1 className="text-[34px] font-bold uppercase tracking-tight mt-2 leading-tight">
              Welcome,<br />Professor.
            </h1>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
              Sign in to manage your academy — schedule, roster, attendance, and announcements.
            </p>
            {pending && (
              <div className="card p-3 mt-6 flex items-start gap-2" style={{ borderColor: "var(--warn-border, #f4b942)", background: "rgba(244,185,66,0.06)" }} data-testid="owner-pending-notice">
                <AlertCircle size={14} style={{ color: "var(--warn, #f4b942)", marginTop: 2 }} />
                <div className="label-tiny leading-relaxed" style={{ color: "var(--text)" }}>
                  <b>{pendingEmail}</b> hasn't been invited to an academy yet. Ask your academy owner to invite you from the Team page.
                </div>
              </div>
            )}
            {error && !pending && (
              <div className="card p-3 mt-6 flex items-start gap-2" style={{ borderColor: "var(--danger, #ff5c5c)", background: "rgba(255,92,92,0.06)" }} data-testid="owner-signin-error">
                <AlertCircle size={14} style={{ color: "var(--danger, #ff5c5c)", marginTop: 2 }} />
                <div className="label-tiny leading-relaxed" style={{ color: "var(--text)" }}>
                  {ERROR_COPY[error] || "Sign-in failed. Please try again."}
                </div>
              </div>
            )}
            <button onClick={googleSignInOwner} className="btn btn-primary w-full mt-10" data-testid="owner-google-signin">
              Continue with Google <ArrowRight size={14} />
            </button>
            <div className="label-tiny text-center mt-5" style={{ color: "var(--text-dim)" }}>
              By continuing you agree to the <Link to="/terms" className="underline" data-testid="link-terms">Terms</Link> and <Link to="/privacy" className="underline" data-testid="link-privacy">Privacy Policy</Link>.
            </div>
            <div className="text-center mt-8 pt-8" style={{ borderTop: "1px solid var(--border)" }}>
              <div className="label-tiny mb-2">Not an owner?</div>
              <Link to="/student/login" className="text-[13px] font-semibold" style={{ color: "var(--green)" }} data-testid="link-student-app">
                Go to the student app →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
